import * as React from "react";
import ActionModal from "../../../modals/smart/action-modal/action-modal";
import BuyTemplateModal, {
    BuyTemplateModalResponse,
    TemplateInfo,
} from "../../../modals/smart/buy-template-modal/buy-template-modal";
import { MissingTablesAndColumns } from "common/Database";
import { CompatibilityWrapper } from "sharedUI/style";
import {
    TypeSchema,
    makeTableName,
    findTable,
    getTableName,
    TableGlideType,
    getTableColumn,
    getTableColumnDisplayName,
} from "common/generator/description";
import { getDocURL } from "common/docUrl";
import PaymentInfoModal from "designer/modals/smart/payment-info-modal/payment-info-modal";
import WindowedModalV2 from "designer/modals-v2/windowed-modal/windowed-modal";
import { frontendTrace } from "common/tracing";
import { getModals } from "designer/modals-v2/modal-provider-v2/externalAPI";
import { PaymentRequestInfo } from "designer/modals/smart/payment-info-modal/stripe-pay-button";

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showBasicActionModal(
    header: string,
    description: React.ReactNode,
    acceptText: string,
    canCancel: boolean = true
): Promise<void> {
    return getModals().showModal<void>(resolve => {
        return (
            <WindowedModalV2 canClose={canCancel} onClose={resolve} hideClosingButton={true}>
                <ActionModal
                    header={header}
                    description={description}
                    acceptText={acceptText}
                    acceptAction={() => resolve()}
                />
            </WindowedModalV2>
        );
    });
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showDestroyCancelModal(
    header: React.ReactNode,
    description: React.ReactNode,
    destroyText: string,
    cancelText: string
): Promise<boolean> {
    return getModals().showModal<boolean>(resolve => {
        return (
            <WindowedModalV2 canClose={true} onClose={() => resolve(false)} hideClosingButton={true}>
                <ActionModal
                    header={header}
                    description={description}
                    cancelText={cancelText}
                    destroyText={destroyText}
                    destroyAction={() => resolve(true)}
                    cancelAction={() => resolve(false)}
                />
            </WindowedModalV2>
        );
    });
}

interface AcceptCheckboxModalResolution {
    checkboxChecked: boolean;
    accept: boolean;
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showAcceptCancelCheckboxModal(
    header: string,
    description: React.ReactNode,
    checkboxText: string,
    acceptText: string,
    cancelText?: string
): Promise<AcceptCheckboxModalResolution> {
    return getModals().showModal<AcceptCheckboxModalResolution>(resolve => {
        return (
            <WindowedModalV2
                canClose={false}
                onClose={() => resolve({ checkboxChecked: false, accept: false })}
                hideClosingButton={true}>
                <ActionModal
                    header={header}
                    description={description}
                    checkboxText={checkboxText}
                    acceptText={acceptText}
                    cancelText={cancelText}
                    acceptAction={checked =>
                        resolve({
                            checkboxChecked: checked,
                            accept: true,
                        })
                    }
                    cancelAction={
                        cancelText === undefined
                            ? undefined
                            : checked =>
                                  resolve({
                                      checkboxChecked: checked,
                                      accept: false,
                                  })
                    }
                />
            </WindowedModalV2>
        );
    });
}

interface DestroyCheckboxModalResolution {
    checkboxChecked: boolean;
    destroy: boolean;
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showDestroyCancelCheckboxModal(
    header: string,
    description: React.ReactNode,
    checkboxText: string,
    destroyText: string,
    cancelText?: string
): Promise<DestroyCheckboxModalResolution> {
    return getModals().showModal<DestroyCheckboxModalResolution>(resolve => {
        return (
            <WindowedModalV2
                canClose={false}
                onClose={() => resolve({ checkboxChecked: false, destroy: false })}
                hideClosingButton={true}>
                <ActionModal
                    header={header}
                    description={description}
                    checkboxText={checkboxText}
                    destroyText={destroyText}
                    cancelText={cancelText}
                    destroyAction={checked =>
                        resolve({
                            checkboxChecked: checked,
                            destroy: true,
                        })
                    }
                    cancelAction={
                        cancelText === undefined
                            ? undefined
                            : checked =>
                                  resolve({
                                      checkboxChecked: checked,
                                      destroy: false,
                                  })
                    }
                />
            </WindowedModalV2>
        );
    });
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showAcceptCancelModal(
    header: string,
    description: React.ReactNode,
    acceptText: string,
    cancelText: string
): Promise<boolean> {
    return getModals().showModal<boolean>(resolve => {
        return (
            <WindowedModalV2 canClose={false} onClose={() => resolve(false)} hideClosingButton={true}>
                <ActionModal
                    header={header}
                    description={description}
                    acceptText={acceptText}
                    cancelText={cancelText}
                    acceptAction={() => resolve(true)}
                    cancelAction={() => resolve(false)}
                />
            </WindowedModalV2>
        );
    });
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showPaymentModal(
    setupIntentClientSecret: string,
    paymentRequestInfo: PaymentRequestInfo
): Promise<string | false> {
    return getModals().showModal<string | false>(resolve => {
        return (
            <WindowedModalV2 canClose={false} hideClosingButton={true} onClose={() => resolve(false)}>
                <PaymentInfoModal
                    setupIntentClientSecret={setupIntentClientSecret}
                    onCompletion={resolve}
                    paymentRequestInfo={paymentRequestInfo}
                />
                ,
            </WindowedModalV2>
        );
    });
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showErrorModal(message: string): Promise<void> {
    await frontendTrace("showErrorModal", { message }, () => showBasicActionModal("Oh sheet!", message, "OK"), true);
}

/**
 * @deprecated use modals from `modal-provider-v2`
 *
 * Only used from a non-unified pricing action
 */
export function showUpgradeSuccessModal(title: string | undefined): void {
    const showTitle = title === undefined ? "Your app" : `'${title}'`;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    showBasicActionModal(
        "Thank you for upgrading",
        <div>
            {showTitle} now has access to{" "}
            <a href="https://glideapps.com/pro#summary" target="_blank" rel="noopener noreferrer">
                all Pro features
            </a>
            .
        </div>,
        "OK"
    );
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showFailureModal(description: string, canCancel: boolean = true): Promise<void> {
    await showBasicActionModal("There's a problem", description, "OK", canCancel);
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showSuccessOrFailureModal(
    success: boolean,
    successDescription: string,
    failureDescription: string
): Promise<void> {
    if (success) {
        await showBasicActionModal("Success", successDescription, "OK");
    } else {
        await showFailureModal(failureDescription);
    }
}

/**
 * @deprecated use modals from `modal-provider-v2`
 *
 * FIXME:
 * Can be consumed by a shared component/hook between `apps.tsx` and `app-settings-inner.tsx`
 */
export async function showSharedDriveNeedsProModal(): Promise<boolean> {
    return getModals().showModal<boolean>(resolve => {
        return (
            <WindowedModalV2 canClose={true} onClose={() => resolve(false)} hideClosingButton={true}>
                <ActionModal
                    header="Google Workspace Shared Drives is a Pro app feature"
                    description="You can still build your app in Glide, but you must upgrade the app to Pro before you can share it."
                    acceptText="OK"
                    acceptAction={() => resolve(false)}
                    informationText="Upgrade app to Pro"
                    informationAction={() => resolve(true)}
                    informationActionClosesModal={false}
                />
            </WindowedModalV2>
        );
    });
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showCompatibilityProblemsModal(
    header: string,
    destroyText: string,
    description: React.ReactNode
): Promise<boolean> {
    return getModals().showModal<boolean>(resolve => {
        return (
            <WindowedModalV2 canClose={false} hideClosingButton={true} onClose={() => resolve(false)}>
                <ActionModal
                    header={header}
                    description={description}
                    cancelText="Cancel"
                    destroyText={destroyText}
                    cancelAction={() => resolve(false)}
                    destroyAction={() => resolve(true)}
                />
            </WindowedModalV2>
        );
    });
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showCompatibilityMissingModal(
    schema: TypeSchema | undefined,
    missing: MissingTablesAndColumns,
    header: string,
    introText: string,
    destroyText: string,
    explanation: string
): Promise<boolean> {
    function getTable(n: string): TableGlideType | undefined {
        if (schema === undefined) return undefined;
        return findTable(schema, makeTableName(n));
    }

    function tableName(table: TableGlideType | undefined, n: string): string {
        if (table === undefined) return n;
        return getTableName(table).name;
    }

    function columnName(t: TableGlideType | undefined, n: string): string {
        if (t === undefined) return n;
        const c = getTableColumn(t, n);
        if (c === undefined) return n;
        return getTableColumnDisplayName(c);
    }

    return await showCompatibilityProblemsModal(
        header,
        destroyText,
        <CompatibilityWrapper>
            <div>{introText}</div>
            <ul className="cw-problem-list">
                {missing.columns.map(({ table, columns }) => {
                    const t = getTable(table);
                    return (
                        <li key={table}>
                            <b>{tableName(t, table)}</b>: {columns.map(c => columnName(t, c)).join(", ")}
                        </li>
                    );
                })}
                {missing.tables.map(table => {
                    const t = getTable(table);
                    return (
                        <li key={table}>
                            <b>{tableName(t, table)}</b>: All columns
                        </li>
                    );
                })}
            </ul>
            <div>{explanation}</div>
        </CompatibilityWrapper>
    );
}

/**
 * @deprecated use modals from `modal-provider-v2`
 */
export async function showBuyOrDownloadTemplateModal(
    templateID: string,
    templateInfo: TemplateInfo
): Promise<BuyTemplateModalResponse> {
    return getModals().showModal<BuyTemplateModalResponse>(resolve => {
        return (
            <WindowedModalV2
                canClose={false}
                onClose={() => resolve({ ownerID: "", nextAction: "do-nothing" })}
                hideClosingButton={true}>
                <BuyTemplateModal templateID={templateID} templateInfo={templateInfo} followUpAction={resolve} />
            </WindowedModalV2>
        );
    });
}

/**
 * @deprecated use modals from `modal-provider-v2`
 *
 * FIXME:
 * Can be consumed by a shared component/hook between builder/index.tsx and user-specific-column-configurator.tsx
 */
export async function showRowIDModal(explanation: string): Promise<boolean> {
    return getModals().showModal<boolean>(resolve => {
        return (
            <WindowedModalV2 canClose={false} onClose={() => resolve(false)} hideClosingButton={true}>
                <ActionModal
                    header="Your sheet needs a Row ID column first."
                    description={explanation}
                    acceptText="Add Row ID"
                    cancelText="Cancel"
                    acceptAction={() => resolve(true)}
                    cancelAction={() => resolve(false)}
                    informationText="Learn more"
                    informationAction={() => {
                        window.open(getDocURL("rowIDConfig"), "_blank");
                    }}
                    informationActionClosesModal={false}
                />
            </WindowedModalV2>
        );
    });
}
